"use client";

import { useState } from "react";

import Input from "@/components/library/Input";
import Modal from "@/components/library/Modal";
import { VoteUrgency } from "@/types/integrations";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { type Importance, IMPORTANCE_OPTIONS, IMPORTANCE_TO_URGENCY } from "./importance-config";

interface IntegrationVoteModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (request: {
    integrationName: string;
    useCase: string;
    urgency: VoteUrgency;
  }) => Promise<void>;
}

export default function IntegrationVoteModal({
  open,
  onClose,
  onSubmit,
}: Readonly<IntegrationVoteModalProps>) {
  const { t: ct } = useTranslation("common");
  const { t } = useTranslation("partners");
  const [integrationName, setIntegrationName] = useState("");
  const [useCase, setUseCase] = useState("");
  const [importance, setImportance] = useState<Importance>("important");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setIntegrationName("");
      setUseCase("");
      setImportance("important");
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!integrationName.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        integrationName,
        useCase: useCase.trim(),
        urgency: IMPORTANCE_TO_URGENCY[importance],
      });
      handleOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = integrationName.trim().length > 0;
  const submitLabel = isSubmitting ? t("marketplaceSubmitting") : t("marketplaceSubmitRequest");

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      header={{
        title: t("marketplaceRequestTitle"),
        description: t("marketplaceRequestDescription"),
      }}
      size="md"
      buttons={[
        {
          type: "primary",
          onClick: () => {
            handleSubmit();
          },
          disabled: !isValid || isSubmitting,
          children: submitLabel,
        },
        {
          type: "secondary",
          onClick: () => handleOpenChange(false),
          children: ct("cancel"),
        },
      ]}
    >
      <div className="space-y-6">
        <div>
          <label
            htmlFor="integration-name"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
          >
            {t("marketplaceFeatureName")} <span className="text-red-500 ml-0.5">*</span>
          </label>
          <Input
            id="integration-name"
            type="text"
            value={integrationName}
            onChange={(e) => setIntegrationName(e.target.value)}
            placeholder={t("marketplaceFeaturePlaceholder")}
          />
        </div>

        <div>
          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t("marketplaceImportanceQuestion")}
            </legend>
            <div className="flex flex-wrap gap-2">
              {IMPORTANCE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setImportance(option.value)}
                  className={clsx(
                    "px-3 py-2 text-xs font-medium rounded-md border transition-all duration-200 uppercase",
                    importance === option.value
                      ? "bg-gailBlue text-white border-gailBlue shadow-sm"
                      : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:border-gailBlue hover:text-gailBlue"
                  )}
                >
                  {t(option.labelKey)}
                </button>
              ))}
            </div>
          </fieldset>
        </div>

        <div>
          <label
            htmlFor="use-case"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
          >
            {t("marketplaceWhyNeed")}
          </label>
          <textarea
            id="use-case"
            value={useCase}
            onChange={(e) => setUseCase(e.target.value)}
            placeholder={t("marketplaceUseCasePlaceholder")}
            rows={4}
            className={clsx(
              "w-full px-3 py-2 text-sm",
              "border border-gray-300 dark:border-gray-600 rounded-lg",
              "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100",
              "placeholder:text-gray-400 dark:placeholder:text-gray-500",
              "focus:ring-2 focus:ring-gailBlue focus:border-gailBlue",
              "transition-colors duration-200"
            )}
          />
        </div>
      </div>
    </Modal>
  );
}
